import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('tuprima_token'));
  const [loading, setLoading] = useState(true);

  const logout = useCallback(() => {
    localStorage.removeItem('tuprima_token');
    setToken(null);
    setUser(null);
  }, []);

  useEffect(() => {
    const fetchUser = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
        } catch (error) {
          console.error('Error fetching user:', error);
          logout();
        }
      }
      setLoading(false);
    };

    fetchUser();
  }, [token, logout]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('tuprima_token', access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const register = async (userData) => {
    const response = await axios.post(`${API}/auth/register`, userData);
    const { access_token, user: newUser } = response.data;
    localStorage.setItem('tuprima_token', access_token);
    setToken(access_token);
    setUser(newUser);
    return newUser;
  };

  const verifyPhone = async (code) => {
    await axios.post(`${API}/auth/verify-phone`, { code }, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setUser(prev => ({ ...prev, is_verified: true }));
  };

  const resendCode = async () => {
    const response = await axios.post(`${API}/auth/resend-code`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    verifyPhone,
    resendCode,
    isAuthenticated: !!user,
    isAdmin: user?.is_admin || false,
    isVerified: user?.is_verified || false,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
